import { useCallback, useEffect, useRef, useState } from 'react';
import { audioContext } from '@/audio/context';
import { Listener } from '@/speech/audio/listener';
import { openMicrophone, type AudioInput } from '@/speech/audio/sources';
import { TOPIC_MODEL } from '@/speech/understand/models';
import { sharedUnderstanding } from '@/speech/understand/understanding';

export type ListeningState =
  | { kind: 'off' }
  | { kind: 'loading'; percent: number }
  | { kind: 'listening' }
  | { kind: 'thinking' }
  | { kind: 'error'; message: string };

/**
 * The microphone for voice control on any page: the child turns it on (a tap: browsers need a
 * gesture), speech-to-text loads once for the whole game (the shared Understanding), and every
 * utterance's transcript goes to `onHeard`. While the game talks it is muted (`setMuted`), so it
 * never hears itself. It closes when the page is hidden or the component unmounts.
 */
export function useListening(onHeard: (text: string) => void | Promise<void>) {
  const [state, setState] = useState<ListeningState>({ kind: 'off' });
  const [heard, setHeard] = useState('');
  const live = useRef<{ listener: Listener; input: AudioInput } | null>(null);
  const handler = useRef(onHeard);
  useEffect(() => {
    handler.current = onHeard;
  }, [onHeard]);

  const stop = useCallback(async () => {
    const current = live.current;
    live.current = null;
    setState({ kind: 'off' });
    if (current) {
      await current.listener.close();
      current.input.close();
    }
  }, []);

  const start = useCallback(async () => {
    if (live.current) return;
    const u = sharedUnderstanding();
    const unsubscribe = u.onProgress((part, loaded, total) => {
      if (part === 'stt' && total > 0)
        setState({ kind: 'loading', percent: Math.round((100 * loaded) / total) });
    });
    try {
      const context = audioContext();
      if (context.state === 'suspended') await context.resume();
      await Listener.prepare(context);
      if (!u.isReady('stt')) setState({ kind: 'loading', percent: 0 });
      const loading = u.load('stt');
      // The classifier after speech-to-text (one load at a time), ready for the first answer.
      if (TOPIC_MODEL.use) void loading.then(() => u.load('topic')).catch(() => undefined);
      const input = await openMicrophone();
      const listener = await Listener.start(
        input.stream,
        {
          onUtterance: (utterance) => {
            setState({ kind: 'thinking' });
            u.transcribe(utterance.audio)
              .then(({ text }) => {
                setHeard(text);
                if (live.current) setState({ kind: 'listening' });
                if (text) void handler.current(text);
              })
              .catch(() => {
                if (live.current) setState({ kind: 'listening' });
              });
          },
        },
        context,
      );
      const session = { listener, input };
      live.current = session;
      await loading;
      // Still on (not stopped while speech-to-text loaded).
      if (live.current === session) setState({ kind: 'listening' });
    } catch (error) {
      await stop();
      setState({
        kind: 'error',
        message: error instanceof Error ? error.message : 'The microphone did not start.',
      });
    } finally {
      unsubscribe();
    }
  }, [stop]);

  /** Mute while the game talks, so the microphone never hears the game. */
  const setMuted = useCallback((muted: boolean) => {
    live.current?.listener.setMuted(muted);
  }, []);

  // Release the microphone when the app is hidden or the page closes.
  useEffect(() => {
    const onVisibility = () => {
      if (document.hidden) void stop();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      void stop();
    };
  }, [stop]);

  return { state, heard, start, stop, setMuted };
}
